// Contas e regras da página "Onde a Hydrodata ganha" (sem tela, sem antd, sem rede).
// Tudo aqui é puro: recebe o que o servidor devolveu e entrega o que a tela desenha.
// O teste está em ondeGanhaRegras.test.ts.
import type {
  Caso, CandidatoSemana3, Grupo, NumReuniao, NumReuniaoTotal, Numeros, NumSituacao, Retrato,
} from './api';
import type { DreSetores, Faturamento12m } from '../administrativo-plano-acao/paginas/apiDre';
import { chaveReceita, margemPct, setoresDaResposta, valoresAcumulados } from '../administrativo-plano-acao/paginas/dadosSetores';
import { metaSetor } from '../../utils/setoresDre';

// ── Formatação ───────────────────────────────────────────────────────────────
/** Número inteiro com ponto no milhar: 21689 vira "21.689". */
export function inteiroBr(v: number | null | undefined): string {
  if (v == null) return '—';
  const n = Math.round(Number(v));
  if (!Number.isFinite(n)) return '—';
  const txt = String(Math.abs(n)).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return (n < 0 ? '-' : '') + txt;
}

/** "R$ 21.689" (sem centavos). Sem valor: "—". */
export function reais(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(Number(v))) return '—';
  const n = Math.round(Number(v));
  return (n < 0 ? '-' : '') + 'R$ ' + inteiroBr(Math.abs(n));
}

/** Rótulo curto de eixo e de tooltip: "R$ 4,13 mi", "R$ 212 mil", "R$ 800". */
export function reaisCurto(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(Number(v))) return '—';
  const n = Number(v), a = Math.abs(n), s = n < 0 ? '-' : '';
  if (a >= 1e6) return `${s}R$ ${(a / 1e6).toFixed(2).replace('.', ',')} mi`;
  if (a >= 1e3) return `${s}R$ ${Math.round(a / 1e3)} mil`;
  return `${s}R$ ${Math.round(a)}`;
}

/** "55%" (já inteiro ou arredondado). Sem valor: "—". */
export function pct(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(Number(v))) return '—';
  return `${Math.round(Number(v))}%`;
}

/** Quantos % `parte` é de `todo`, inteiro. Todo zero: null. */
export function pctDe(parte: number, todo: number): number | null {
  return todo > 0 ? Math.round((parte / todo) * 100) : null;
}

/** "8 (67%)": quantos fecharam e que parte isso é do grupo. Grupo vazio: "—". */
export function fechouTexto(ganhos: number, casos: number): string {
  const p = pctDe(ganhos, casos);
  return p == null ? '—' : `${ganhos} (${p}%)`;
}

/** "3 de 14 (21%)": a mesma conta, escrita por extenso. Grupo vazio: "—". */
export function fechouDeTexto(ganhos: number, casos: number): string {
  const p = pctDe(ganhos, casos);
  return p == null ? '—' : `${ganhos} de ${casos} (${p}%)`;
}

/**
 * "Os 3 maiores clientes ganhos = 79%": quanto os 3 maiores clientes pesam na receita da situação.
 * Não diz quantos clientes são: o servidor conta também os clientes só com perda, e o percentual é só dos ganhos.
 */
export function textoTop3(top3Pct: number): string {
  return `Os 3 maiores clientes ganhos = ${pct(top3Pct)}`;
}

/** Frase em português para falha sem mensagem do servidor (rede caída, tempo esgotado, 502/504 do gateway). */
export function fraseDeFalha(status: number | null, houveResposta: boolean, codigo?: string | null, mensagem?: string | null): string {
  if (!houveResposta) {
    if (codigo === 'ECONNABORTED' || codigo === 'ETIMEDOUT' || /timeout/i.test(String(mensagem || ''))) {
      return 'O servidor demorou demais para responder. Tente de novo em instantes.';
    }
    return 'Sem conexão com o servidor. Confira a internet e tente de novo.';
  }
  if (status === 401 || status === 403) return 'Sua sessão venceu ou você não tem acesso a esta ação. Entre de novo e tente outra vez.';
  if (status === 408 || status === 504) return 'O servidor demorou demais para responder. Tente de novo em instantes.';
  if (status != null && status >= 500) return 'O servidor teve um problema. Tente de novo em instantes.';
  return 'Não deu certo. Tente de novo em instantes.';
}

/** Quantas consultas seguidas podem falhar antes de a tela parar de insistir. */
export const MAX_FALHAS_SEGUIDAS = 10;

/** Se o filtro de situação já não existe nas opções (o caso sumiu do grupo), volta para "todas". */
export function filtroValido(filtro: string, opcoes: { value: string }[]): string {
  return opcoes.some((o) => o.value === filtro) ? filtro : TODAS_AS_SITUACOES;
}

// ── Datas (por componentes; nunca toISOString().slice para exibir) ───────────
/** "2026-10-16" ou "2026-10-16T00:00:00.000Z" vira "16/10/2026". Texto inesperado volta como veio. */
export function dataBr(v: string | null | undefined): string {
  if (!v) return '—';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(v));
  return m ? `${m[3]}/${m[2]}/${m[1]}` : String(v);
}

const doisDigitos = (n: number): string => String(n).padStart(2, '0');

interface Partes { dia: number; mes: number; hora: number; minuto: number }

/** Dia e hora no fuso de Brasília (UTC-3 o ano todo desde 2019; o Intl cobre o resto). */
function partesEmBrasilia(iso: string): Partes | null {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  try {
    const f = new Intl.DateTimeFormat('pt-BR', {
      timeZone: 'America/Sao_Paulo', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
    });
    const p: Record<string, number> = {};
    f.formatToParts(d).forEach((x) => { if (x.type !== 'literal') p[x.type] = Number(x.value); });
    if ([p.day, p.month, p.hour, p.minute].every((x) => Number.isFinite(x))) {
      return { dia: p.day, mes: p.month, hora: p.hour, minuto: p.minute };
    }
  } catch { /* sem Intl com fuso: usa a conta direta abaixo */ }
  const b = new Date(d.getTime() - 3 * 3600 * 1000);
  return { dia: b.getUTCDate(), mes: b.getUTCMonth() + 1, hora: b.getUTCHours(), minuto: b.getUTCMinutes() };
}

/** "16/10 às 14h32" (hora de Brasília). Inválido: "—". */
export function textoQuando(iso: string | null | undefined): string {
  if (!iso) return '—';
  const p = partesEmBrasilia(iso);
  if (!p) return '—';
  return `${doisDigitos(p.dia)}/${doisDigitos(p.mes)} às ${doisDigitos(p.hora)}h${doisDigitos(p.minuto)}`;
}

/** Parte do e-mail antes do "@": "gustavo@hydro.com" vira "gustavo". */
export function quemPediu(email: string | null | undefined): string {
  const t = String(email || '').trim();
  if (!t) return 'alguém da equipe';
  const i = t.indexOf('@');
  return i > 0 ? t.slice(0, i) : t;
}

/** Linha de estado do cabeçalho: de quando é o texto e quem pediu. */
export function linhaDoRetrato(retrato: Retrato | null): string {
  if (!retrato) return 'Ainda não há texto da IA.';
  if (retrato.origem === 'importado') return 'Importado da página de 16/10/2026';
  return `Texto da IA de ${textoQuando(retrato.concluido_em || retrato.pedido_em)}, pedido por ${quemPediu(retrato.pedido_por)}`;
}

// ── Os números mudaram desde o texto? ────────────────────────────────────────
/** true quando há texto da IA e a conta de hoje já não é a que ele leu. Sem um dos dois lados: false. */
export function numerosMudaram(retrato: Pick<Retrato, 'assinatura'> | null | undefined, assinaturaAtual: string | null | undefined): boolean {
  if (!retrato || !retrato.assinatura || !assinaturaAtual) return false;
  return retrato.assinatura !== assinaturaAtual;
}

// ── Lista de casos: filtro ───────────────────────────────────────────────────
/** Minúsculas e sem acento, para a busca achar "licenca" em "Licença". */
export function semAcento(t: string | null | undefined): string {
  return String(t || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

export const TODAS_AS_SITUACOES = '__todas';
export const SEM_SITUACAO = '__sem_situacao';
export type FiltroStatus = 'todos' | 'ganho' | 'perdido';
export interface FiltroCasos { grupo?: string | null; status?: FiltroStatus; busca?: string }

/** Filtra por situação, ganho/perdido e texto livre (título, cliente, linha e segmento). */
export function filtrarCasos(casos: Caso[], f: FiltroCasos): Caso[] {
  const grupo = f.grupo && f.grupo !== TODAS_AS_SITUACOES ? f.grupo : null;
  const status = f.status && f.status !== 'todos' ? f.status : null;
  const palavras = semAcento(f.busca).split(/\s+/).filter(Boolean);
  return casos.filter((c) => {
    if (grupo === SEM_SITUACAO) { if (c.grupo) return false; } else if (grupo && c.grupo !== grupo) return false;
    if (status && c.status !== status) return false;
    if (palavras.length) {
      const palheiro = semAcento(`${c.titulo} ${c.cliente || ''} ${c.linha || ''} ${c.segmento}`);
      if (!palavras.every((p) => palheiro.includes(p))) return false;
    }
    return true;
  });
}

export interface ContagemCasos { casos: number; ganhos: number; perdidos: number; taxa: number | null }

/** Contador da lista: quantos casos, quantos ganhos e perdidos e quanto por cento fechou. */
export function contarCasos(casos: Pick<Caso, 'status'>[]): ContagemCasos {
  const ganhos = casos.filter((c) => c.status === 'ganho').length;
  const perdidos = casos.filter((c) => c.status === 'perdido').length;
  return { casos: casos.length, ganhos, perdidos, taxa: pctDe(ganhos, casos.length) };
}

/** "110 casos · 61 ganhos · 49 perdidos · fecha 55%" */
export function textoContagem(c: ContagemCasos): string {
  const partes = [`${c.casos} ${c.casos === 1 ? 'caso' : 'casos'}`, `${c.ganhos} ${c.ganhos === 1 ? 'ganho' : 'ganhos'}`, `${c.perdidos} ${c.perdidos === 1 ? 'perdido' : 'perdidos'}`];
  if (c.taxa != null) partes.push(`fecha ${c.taxa}%`);
  return partes.join(' · ');
}

// ── Grupos (as situações) ────────────────────────────────────────────────────
/** Nome da situação: o curto (tabelas e gráficos) ou o por extenso. Sem grupo: "Sem situação". */
export function rotuloGrupo(grupos: Grupo[], codigo: string | null | undefined, curto = true): string {
  if (!codigo) return 'Sem situação';
  const g = grupos.find((x) => x.codigo === codigo);
  if (!g) return codigo;
  return curto ? g.curto : g.nome;
}

/** Opções do Select de situação com a contagem de cada uma ("Projetista precisa de dado de campo (110)"). */
export function opcoesDeGrupo(grupos: Grupo[], casos: Pick<Caso, 'grupo'>[]): { value: string; label: string }[] {
  const conta: Record<string, number> = {};
  let sem = 0;
  casos.forEach((c) => { if (c.grupo) conta[c.grupo] = (conta[c.grupo] || 0) + 1; else sem += 1; });
  const lista = [{ value: TODAS_AS_SITUACOES, label: `Todas as situações (${casos.length})` }];
  grupos.forEach((g) => { if (conta[g.codigo]) lista.push({ value: g.codigo, label: `${g.curto} (${conta[g.codigo]})` }); });
  if (sem) lista.push({ value: SEM_SITUACAO, label: `Sem situação (${sem})` });
  return lista;
}

export const LIMITE_LINHA = 300;
export const MINIMO_LINHA = 3;

/** Frase de erro para a edição de um caso, ou null quando pode salvar. */
export function erroDaEdicao(grupo: string | null | undefined, linha: string | null | undefined, grupos: Grupo[]): string | null {
  if (!grupo || !grupos.some((g) => g.codigo === grupo)) return 'Escolha a situação.';
  const t = String(linha || '').trim();
  if (t.length < MINIMO_LINHA) return `Escreva a linha do caso (pelo menos ${MINIMO_LINHA} letras).`;
  if (t.length > LIMITE_LINHA) return `A linha passou de ${LIMITE_LINHA} letras.`;
  return null;
}

// ── Reunião ──────────────────────────────────────────────────────────────────
export interface TotalReuniao { casos: number; com: number; com_ganhos: number; sem: number; sem_ganhos: number; sem_info: number }

const n0 = (v: any): number => (typeof v === 'number' && Number.isFinite(v) ? v : 0);

/** Linha Total da tabela de reunião: a do servidor; o que faltar nela sai da soma dos segmentos. */
export function totalDaReuniao(r: NumReuniao | null | undefined): TotalReuniao {
  const segs = r?.por_segmento || [];
  const soma = (k: 'casos' | 'com' | 'com_ganhos' | 'sem' | 'sem_ganhos' | 'sem_info'): number => segs.reduce((s, x) => s + n0(x[k]), 0);
  const t: NumReuniaoTotal = r?.total || {};
  const pegar = (k: keyof TotalReuniao): number => (typeof t[k] === 'number' ? (t[k] as number) : soma(k));
  return { casos: pegar('casos'), com: pegar('com'), com_ganhos: pegar('com_ganhos'), sem: pegar('sem'), sem_ganhos: pegar('sem_ganhos'), sem_info: pegar('sem_info') };
}

export const ROTULO_RELACAO: Record<string, string> = { novo: 'Cliente novo', casa: 'Cliente que já comprou' };

// ── Situações: tabelas e gráficos ────────────────────────────────────────────
export interface BarraSituacao {
  grupo: string; nome: string; casos: number; ganhos: number; perdidos: number; taxa: number;
  receita: number; clientes: number; top3_pct: number;
}

/** Uma barra por situação que tem caso, na ordem do servidor (mais casos primeiro), com o nome curto. */
export function barrasDeSituacao(numeros: Pick<Numeros, 'situacoes'> | null | undefined, grupos: Grupo[]): BarraSituacao[] {
  return (numeros?.situacoes || [])
    .filter((s) => s.casos > 0)
    .map((s) => ({
      grupo: s.grupo, nome: rotuloGrupo(grupos, s.grupo), casos: s.casos, ganhos: s.ganhos, perdidos: s.perdidos,
      taxa: s.taxa, receita: s.receita, clientes: s.clientes, top3_pct: s.top3_pct,
    }));
}

/** As `n` situações com mais casos (a tabela do item 3). */
export function maioresSituacoes(numeros: Pick<Numeros, 'situacoes'> | null | undefined, n = 5): NumSituacao[] {
  return (numeros?.situacoes || []).filter((s) => s.casos > 0).slice(0, n);
}

/** Leitura da IA para uma situação (ou null). */
export function leituraDaSituacao(texto: { situacoes: { grupo: string; leitura: string }[] } | null | undefined, grupo: string): string | null {
  return texto?.situacoes?.find((s) => s.grupo === grupo)?.leitura || null;
}

// ── Semana 3 ─────────────────────────────────────────────────────────────────
export interface NotasDoCandidato { soma: number; completa: boolean }

/** Soma das quatro notas. Capacidade "a decidir" (null) não entra e deixa a soma incompleta. */
export function somaDasNotas(c: CandidatoSemana3): NotasDoCandidato {
  const cap = c.capacidade?.nota;
  const soma = n0(c.aceite?.nota) + n0(c.posicionamento?.nota) + n0(c.timing?.nota) + (typeof cap === 'number' ? cap : 0);
  return { soma, completa: typeof cap === 'number' };
}

/** Candidatos do maior para o menor total; empate mantém a ordem que a IA deu. */
export function ordenarCandidatos(lista: CandidatoSemana3[] | null | undefined): CandidatoSemana3[] {
  return (lista || [])
    .map((c, i) => ({ c, i, s: somaDasNotas(c).soma }))
    .sort((a, b) => b.s - a.s || a.i - b.i)
    .map((x) => x.c);
}

// ── Botão "Atualizar com IA": máquina de estados ─────────────────────────────
export type Fase = 'parado' | 'enviando' | 'acompanhando' | 'pronto' | 'erro';
export type Evento = 'clicou' | 'aceito' | 'recusado' | 'terminou' | 'falhou' | 'retomou';

/**
 * parado → enviando → acompanhando → pronto | erro.
 * - clicou: pede a atualização (de parado, pronto ou erro);
 * - aceito: o servidor aceitou, agora é só acompanhar;
 * - recusado: o servidor disse não (já tem uma em andamento, ou espere N min): volta a parado, a mensagem fica na tela;
 * - terminou / falhou: a consulta viu o fim;
 * - retomou: a página abriu e já havia uma atualização rodando.
 * Evento que não cabe na fase atual é ignorado.
 */
export function proximoPasso(estado: Fase, evento: Evento): Fase {
  switch (estado) {
    case 'parado':
    case 'pronto':
    case 'erro':
      if (evento === 'clicou') return 'enviando';
      if (evento === 'retomou') return 'acompanhando';
      return estado;
    case 'enviando':
      if (evento === 'aceito') return 'acompanhando';
      if (evento === 'recusado') return 'parado';
      if (evento === 'falhou') return 'erro';
      return estado;
    case 'acompanhando':
      if (evento === 'terminou') return 'pronto';
      if (evento === 'falhou') return 'erro';
      return estado;
    default:
      return estado;
  }
}

/** A IA está trabalhando: o botão fica travado. */
export const ocupado = (fase: Fase): boolean => fase === 'enviando' || fase === 'acompanhando';

/** Só consulta se estiver acompanhando e a consulta anterior já voltou (evita duas ao mesmo tempo). */
export function podeConsultar(fase: Fase, consultaEmCurso: boolean): boolean {
  return fase === 'acompanhando' && !consultaEmCurso;
}

export const INTERVALO_CONSULTA_MS = 4000;
export const CHAVE_ATUALIZACAO = 'onde_ganha_atualizacao_id';

export const TEXTO_ESPERA = 'A IA está lendo os negócios. Leva de 1 a 3 minutos. Pode sair da página.';
export const TEXTO_SEM_RETRATO = 'Ainda não há texto da IA. Clique em Atualizar com IA.';
export const TEXTO_NUMEROS_MUDARAM = 'Os números mudaram desde o último texto da IA. Clique em Atualizar com IA para o texto acompanhar.';

// ── "A base (semana 1)": faturamento e lucro dos últimos 12 meses fechados ───
export interface BarraBase { chave: string; nome: string; valor: number; cor: string; pct: number | null }
export interface BarraLucro { chave: string; nome: string; lucro: number; receita: number; margem: number | null; cor: string }
export interface ResumoBase {
  faturamento: number | null;
  /** 18/10/2026 (Lucas): linha "Investimentos" da DRE (equipamentos, veículos, software), em positivo. */
  investido: number | null;
  /** Lucro final = lucro líquido − investimentos (linha da DRE). */
  lucroFinal: number | null;
  margemFinal: number | null;
  /** % que os 3 maiores clientes pesam no faturamento. */
  top3Pct: number | null;
  clientes: number | null;
  lucro: number | null;
  /** Margem do lucro líquido sobre a receita bruta, em %. */
  margem: number | null;
  setoresFat: BarraBase[];
  maiores: BarraBase[];
  lucroSetor: BarraLucro[];
  /** Nomes dos 3 maiores clientes, para o texto. */
  nomesTop3: string[];
}

const abs05 = (v: number): boolean => Math.abs(v) >= 0.5;

/** Reúne os números da base. Cada lado pode faltar (a leitura falhou): o que faltar fica vazio. */
export function resumoDaBase(fat: Faturamento12m | null | undefined, dre: DreSetores | null | undefined): ResumoBase {
  const out: ResumoBase = {
    faturamento: null, top3Pct: null, clientes: null, lucro: null, margem: null,
    investido: null, lucroFinal: null, margemFinal: null,
    setoresFat: [], maiores: [], lucroSetor: [], nomesTop3: [],
  };
  if (fat) {
    const total = n0(fat.total);
    const clientes = [...(fat.clientes || [])].filter((c) => abs05(n0(c.total))).sort((a, b) => n0(b.total) - n0(a.total));
    out.faturamento = total;
    out.clientes = clientes.length;
    const top3 = clientes.slice(0, 3);
    out.top3Pct = total > 0 ? (top3.reduce((s, c) => s + n0(c.total), 0) / total) * 100 : null;
    out.nomesTop3 = top3.map((c) => c.nome);
    out.maiores = clientes.slice(0, 8).map((c) => ({
      chave: String(c.chave), nome: c.nome, valor: n0(c.total), cor: '#1677ff', pct: total > 0 ? (n0(c.total) / total) * 100 : null,
    }));
    out.setoresFat = (fat.por_setor || [])
      .filter((p) => abs05(n0(p.valor)))
      .map((p) => {
        const m = metaSetor(p.setor);
        return { chave: m.chave, nome: m.rotulo, valor: n0(p.valor), cor: m.cor, pct: total > 0 ? (n0(p.valor) / total) * 100 : null };
      })
      .sort((a, b) => b.valor - a.valor);
  }
  if (dre) {
    const lucro = valoresAcumulados(dre, 'lucro_liq');
    const receita = valoresAcumulados(dre, chaveReceita(dre.linhas));
    out.lucro = n0(lucro.total);
    out.margem = margemPct(n0(lucro.total), n0(receita.total));
    const temLinha = (k: string) => (dre.linhas || []).some((l: any) => l.chave === k);
    if (temLinha('investimentos')) out.investido = Math.abs(n0(valoresAcumulados(dre, 'investimentos').total));
    if (temLinha('lucro_final')) {
      const lf = n0(valoresAcumulados(dre, 'lucro_final').total);
      out.lucroFinal = lf;
      out.margemFinal = margemPct(lf, n0(receita.total));
    }
    out.lucroSetor = setoresDaResposta(dre.setores, [lucro, receita])
      .filter((s) => abs05(n0(lucro[s])) || abs05(n0(receita[s])))
      .map((s) => {
        const m = metaSetor(s);
        return { chave: m.chave, nome: m.rotulo, lucro: n0(lucro[s]), receita: n0(receita[s]), margem: margemPct(n0(lucro[s]), n0(receita[s])), cor: m.cor };
      });
  }
  return out;
}

/**
 * "O que aprendemos" com o dinheiro da empresa (18/10/2026, pedido do Lucas: "inclua o valor
 * investido e o lucro final"). Feito por regra, sem IA, e só aparece para quem vê a DRE — o texto
 * da IA não recebe número da DRE de propósito.
 */
export function aprendizadosDaBase(r: ResumoBase, periodo: string): string[] {
  const f: string[] = [];
  if (r.faturamento != null && r.lucro != null) {
    f.push(`Faturamos ${reaisCurto(r.faturamento)} em 12 meses${periodo ? ` (${periodo})` : ''}. O lucro líquido foi ${reaisCurto(r.lucro)}${r.margem != null ? ` (${pct(r.margem)} da receita)` : ''}.`);
  }
  if (r.investido != null && r.lucroFinal != null) {
    f.push(`Investimos ${reaisCurto(r.investido)} em equipamentos, veículos e software. Depois disso, o lucro final ficou em ${reaisCurto(r.lucroFinal)}${r.margemFinal != null ? ` (${pct(r.margemFinal)} da receita)` : ''}.`);
  }
  return f;
}

/** Junta nomes em português: "A", "A e B", "A, B e C". */
export function listaEmTexto(nomes: string[]): string {
  if (nomes.length <= 1) return nomes.join('');
  return `${nomes.slice(0, -1).join(', ')} e ${nomes[nomes.length - 1]}`;
}

/**
 * Frases da base, feitas por regra (sem IA). Os números ficam nos cartões e nos gráficos;
 * aqui só aparece o que eles não dizem: quem são os clientes e que setor lidera.
 */
export function frasesDaBase(r: ResumoBase): string[] {
  const f: string[] = [];
  if (r.nomesTop3.length === 3) {
    f.push(`Os três maiores clientes são ${listaEmTexto(r.nomesTop3)}.${r.top3Pct != null && r.top3Pct >= 40 ? ' Perder um deles muda o ano.' : ''}`);
  }
  if (r.setoresFat.length) f.push(`${r.setoresFat[0].nome} é o maior setor do faturamento.`);
  const comMargem = r.lucroSetor.filter((x) => x.margem != null && x.receita > 0 && x.chave !== 'sem_setor');
  if (comMargem.length >= 2) {
    const ord = [...comMargem].sort((a, b) => (b.margem as number) - (a.margem as number));
    f.push(`${ord[0].nome} deixa mais lucro sobre a receita. ${ord[ord.length - 1].nome} deixa menos.`);
  }
  return f;
}
